import { useEffect } from 'react';
import { useLocation } from 'wouter';

import { usePopup } from 'components/Popups';
import { AuthStatus, useClientProfile, useUISettings } from 'store';
import { type ChatHook, ChatVisibilityState } from './types';

const availableRoutes: string[] = ['/', '/casino', '/live-casino'];

const useLiveChat: ChatHook<'livechat'> = ({ license }) => {
  const [{ status, data }] = useClientProfile();
  const [{ showSandwichMenu }] = useUISettings();
  const [pathname] = useLocation();
  const { isPopupVisible: isDefaultPopupVisible } = usePopup('default');
  const { isPopupVisible: isNonBlockingPopupVisible } =
    usePopup('non-blocking');

  const handleVisibility = () => {
    const isInAvailableRoutes = availableRoutes.includes(pathname);

    if (
      isInAvailableRoutes &&
      !showSandwichMenu &&
      !isDefaultPopupVisible &&
      !isNonBlockingPopupVisible
    ) {
      window.LiveChatWidget?.call(ChatVisibilityState.MINIMIZE);
    } else {
      window.LiveChatWidget?.call(ChatVisibilityState.HIDE);
    }
  };

  /*
    Call immediately to ensure the chat is hidden/minimized even after a page reload,
    subscribe to 'ready' as a fallback in case LiveChat hasn't loaded yet
   */
  handleVisibility();

  window.LiveChatWidget?.on('ready', handleVisibility);

  useEffect(() => {
    window.__lc = {
      ...window.__lc,
      license: license,
      integration_name: 'manual_channels',
      product_name: 'livechat',
    };
  }, [license]);

  useEffect(() => {
    if (status === AuthStatus.signedIn) {
      const name = data?.client.firstName;
      const email = data?.client.email;

      if (name) {
        window.LiveChatWidget?.call('set_customer_name', name);
      }
      if (email) {
        window.LiveChatWidget?.call('set_customer_email', email);
      }
    }
  }, [status, data]);

  useEffect(() => {
    const script = document.createElement('script');
    script.innerText =
      ';(function(n,t,c){function i(n){return e._h?e._h.apply(null,n):e._q.push(n)}var e={_q:[],_h:null,_v:"2.0",on:function(){i(["on",c.call(arguments)])},once:function(){i(["once",c.call(arguments)])},off:function(){i(["off",c.call(arguments)])},get:function(){if(!e._h)throw new Error("[LiveChatWidget] You can\'t use getters before load.");return i(["get",c.call(arguments)])},call:function(){i(["call",c.call(arguments)])},init:function(){var n=t.createElement("script");n.async=!0,n.type="text/javascript",n.src="https://cdn.livechatinc.com/tracking.js",t.head.appendChild(n)}};!n.__lc.asyncInit&&e.init(),n.LiveChatWidget=n.LiveChatWidget||e}(window,document,[].slice))';
    document.body.appendChild(script);
  }, []);

  return null;
};

export default useLiveChat;
